import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown, ChevronUp, ChevronsUpDown, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Sheet } from '@/components/atoms/sheet';
import {
  CollectionErrorState,
  DataTableCountFooter,
  DataTableDrawerSheet,
  DataTableEmptyRow,
  DataTablePaginationFooter,
  DataTableSkeletonRows,
  EntityListPagination,
} from '@/components/molecules';
import { DataTableColumnVisibilityList } from '@/components/organisms/DataTableColumnVisibilityList';
import { DataTableToolbar } from '@/components/organisms/DataTableToolbar';
import { usePersistentColumns } from '@/hooks/lib/usePersistentColumns';
import { cn } from '@/lib/utils';

export type EntityDataTableSortDir = 'asc' | 'desc';

const MIN_COLUMN_WIDTH = 80;
const DEFAULT_COLUMN_WIDTH = 160;
const ACTIONS_COLUMN_WIDTH = 48;

export interface EntityDataTableColumn<TKey extends string = string> {
  key: TKey;
  label: string;
  defaultVisible?: boolean;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  headerClassName?: string;
  cellClassName?: string;
  width?: number;
  /** Opt this column out of single-line truncation back to multi-line wrapping. */
  wrap?: boolean;
}

interface EntityDataTablePagination {
  count: number;
  singular: string;
  plural?: string;
  page: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  variant?: 'data-table' | 'entity-list';
  pageSize?: number;
}

interface EntityDataTableProps<TRow, TKey extends string> {
  columns: EntityDataTableColumn<TKey>[];
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  renderCell: (key: TKey, row: TRow, column: EntityDataTableColumn<TKey>) => ReactNode;
  onVisibleColumnsChange?: (columns: EntityDataTableColumn<TKey>[]) => void;
  storageKey: string;
  emptyIcon: LucideIcon;
  emptyTitle: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  showClearAll: boolean;
  onClearAll: () => void;
  activeFilterCount: number;
  filterSheet?: {
    title?: string;
    content: ReactNode;
    onReset: () => void;
    resetDisabled: boolean;
  };
  toolbarActions?: ReactNode;
  exporting?: boolean;
  onExportXLSX?: () => void;
  onExportCSV?: () => void;
  showExport?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  errorTitle?: string;
  onRetry?: () => void;
  skeletonRowCount?: number;
  onRowClick?: (row: TRow) => void;
  rowClassName?: string | ((row: TRow) => string);
  renderRowActions?: (row: TRow) => ReactNode;
  actionCellClassName?: string;
  sortKey?: TKey | null;
  sortDir?: EntityDataTableSortDir | null;
  onSort?: (column: EntityDataTableColumn<TKey>) => void;
  pagination?: EntityDataTablePagination;
  fixedColumnWidths?: boolean;
}

function SortableEntityHeader<TKey extends string>({
  column,
  isFirst,
  width,
  sortDir,
  onSort,
  onResizeStart,
}: {
  column: EntityDataTableColumn<TKey>;
  isFirst: boolean;
  width?: string;
  sortDir: EntityDataTableSortDir | null;
  onSort: () => void;
  onResizeStart: (event: React.MouseEvent, key: TKey) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.key,
  });
  const sortable = Boolean(column.sortable);
  const rightAligned = column.align === 'right';

  return (
    <th
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        width,
        position: isFirst ? 'sticky' : 'relative',
        left: isFirst ? 0 : undefined,
        zIndex: isFirst ? 20 : undefined,
      }}
      className={cn(
        'select-none overflow-hidden bg-muted py-2.5 text-sm font-normal tracking-wide text-muted-foreground',
        isFirst ? 'pl-4 pr-3' : 'px-3',
        isFirst && 'shadow-[1px_0_0_0_var(--border)]',
        rightAligned && 'text-right',
        isDragging && 'rounded bg-muted/60 opacity-50',
        column.headerClassName,
      )}
    >
      <span className={cn('flex items-center gap-1 truncate', rightAligned && 'justify-end')}>
        <span
          {...attributes}
          {...listeners}
          className="shrink-0 cursor-grab active:cursor-grabbing"
          onClick={(event) => event.stopPropagation()}
        >
          <GripVertical size={11} className="text-muted-foreground/40" />
        </span>
        <span
          onClick={sortable ? onSort : undefined}
          className={cn(
            'flex items-center gap-0.5 truncate',
            sortable && 'cursor-pointer transition-colors hover:text-foreground',
          )}
        >
          <span className="truncate">{column.label}</span>
          {sortable ? (
            sortDir === 'asc' ? (
              <ChevronUp size={11} className="shrink-0" />
            ) : sortDir === 'desc' ? (
              <ChevronDown size={11} className="shrink-0" />
            ) : (
              <ChevronsUpDown size={11} className="shrink-0 opacity-30" />
            )
          ) : null}
        </span>
      </span>
      <span
        onMouseDown={(event) => {
          event.stopPropagation();
          onResizeStart(event, column.key);
        }}
        className="absolute right-0 top-0 flex h-full w-3 cursor-col-resize items-center justify-center"
      >
        <span className="h-4 w-px rounded-full bg-border" />
      </span>
    </th>
  );
}

export function EntityDataTable<TRow, TKey extends string>({
  columns,
  rows,
  getRowKey,
  renderCell,
  onVisibleColumnsChange,
  storageKey,
  emptyIcon,
  emptyTitle,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showClearAll,
  onClearAll,
  activeFilterCount,
  filterSheet,
  toolbarActions,
  exporting = false,
  onExportXLSX,
  onExportCSV,
  showExport = false,
  isLoading = false,
  isError = false,
  errorTitle = 'Failed to load records',
  onRetry,
  skeletonRowCount = 10,
  onRowClick,
  rowClassName,
  renderRowActions,
  actionCellClassName = 'whitespace-nowrap border-b border-border/40 pr-4 text-right',
  sortKey,
  sortDir,
  onSort,
  pagination,
  fixedColumnWidths = true,
}: EntityDataTableProps<TRow, TKey>) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const resizeRef = useRef<{ key: TKey; startX: number; startWidth: number } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [measuredActionsWidth, setMeasuredActionsWidth] = useState(ACTIONS_COLUMN_WIDTH);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const persistentColumnConfigs = useMemo(
    () =>
      columns.map((column) => ({
        key: column.key,
        defaultVisible: column.defaultVisible,
        width: column.width,
      })),
    [columns],
  );
  const {
    columnPrefs,
    columnWidths,
    setColumnWidths,
    toggleColumn,
    reorderColumns,
    reorderVisibleColumns,
    resetColumns,
    persistWidths,
  } = usePersistentColumns({
    columns: persistentColumnConfigs,
    storageKey,
  });

  const columnByKey = useMemo(
    () => new Map(columns.map((column) => [column.key, column])),
    [columns],
  );
  const orderedColumns = useMemo(
    () =>
      columnPrefs.order
        .map((key) => columnByKey.get(key))
        .filter((column): column is EntityDataTableColumn<TKey> => Boolean(column)),
    [columnByKey, columnPrefs.order],
  );
  const visibleColumns = useMemo(
    () => orderedColumns.filter((column) => columnPrefs.visible.includes(column.key)),
    [columnPrefs.visible, orderedColumns],
  );
  const hasActions = Boolean(renderRowActions);
  const visibleColumnWidths = useMemo(() => {
    const widths = visibleColumns.map((column) =>
      Math.max(columnWidths[column.key] ?? column.width ?? DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH),
    );
    if (fixedColumnWidths) {
      return new Map(
        visibleColumns.map((column, index) => [
          column.key,
          `${widths[index] ?? MIN_COLUMN_WIDTH}px`,
        ]),
      );
    }

    const totalWeight = widths.reduce((total, width) => total + width, 0);
    return new Map(
      visibleColumns.map((column, index) => [
        column.key,
        totalWeight > 0
          ? `${((widths[index] ?? MIN_COLUMN_WIDTH) / totalWeight) * 100}%`
          : undefined,
      ]),
    );
  }, [columnWidths, fixedColumnWidths, visibleColumns]);
  const tableMinWidth = useMemo(() => {
    if (!fixedColumnWidths) return undefined;
    const columnsWidth = visibleColumns.reduce(
      (total, column) =>
        total +
        Math.max(
          columnWidths[column.key] ?? column.width ?? DEFAULT_COLUMN_WIDTH,
          MIN_COLUMN_WIDTH,
        ),
      0,
    );
    return columnsWidth + (hasActions ? measuredActionsWidth : 0);
  }, [columnWidths, fixedColumnWidths, hasActions, measuredActionsWidth, visibleColumns]);

  useEffect(() => {
    onVisibleColumnsChange?.(visibleColumns);
  }, [onVisibleColumnsChange, visibleColumns]);

  // Auto-size the actions column to its widest rendered content (1 kebab, 4 icon
  // buttons, …) so the cell never overflows its fixed track and pushes the sticky
  // first column over its neighbour. Observes the content nodes (intrinsic width),
  // not the cell, so growing the <col> never re-triggers the observer.
  useEffect(() => {
    const table = tableRef.current;
    if (!table || !hasActions) {
      setMeasuredActionsWidth(ACTIONS_COLUMN_WIDTH);
      return;
    }

    const ACTIONS_CELL_PADDING = 16; // pr-4

    function measure() {
      const nodes = table!.querySelectorAll<HTMLElement>('[data-actions-content]');
      let max = 0;
      nodes.forEach((node) => {
        max = Math.max(max, node.scrollWidth);
      });
      if (nodes.length === 0) return;
      const next = Math.max(ACTIONS_COLUMN_WIDTH, Math.ceil(max + ACTIONS_CELL_PADDING));
      setMeasuredActionsWidth((current) => (current === next ? current : next));
    }

    measure();
    const observer = new ResizeObserver(measure);
    table
      .querySelectorAll<HTMLElement>('[data-actions-content]')
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [hasActions, rows, visibleColumns]);

  function handleColumnsDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    reorderColumns(String(active.id) as TKey, String(over.id) as TKey);
  }

  function handleHeaderDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    reorderVisibleColumns(String(active.id) as TKey, String(over.id) as TKey);
  }

  function handleResizeStart(event: React.MouseEvent, key: TKey) {
    const currentWidth = Math.max(
      columnWidths[key] ?? columnByKey.get(key)?.width ?? DEFAULT_COLUMN_WIDTH,
      MIN_COLUMN_WIDTH,
    );
    resizeRef.current = { key, startX: event.clientX, startWidth: currentWidth };

    function onMouseMove(moveEvent: MouseEvent) {
      if (!resizeRef.current) return;
      const { key, startWidth, startX } = resizeRef.current;
      const width = Math.max(MIN_COLUMN_WIDTH, startWidth + moveEvent.clientX - startX);
      setColumnWidths((current) => ({ ...current, [key]: width }));
    }

    function onMouseUp() {
      setColumnWidths((current) => {
        persistWidths(current);
        return current;
      });
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  const columnDrawerColumns = orderedColumns.map((column) => ({
    key: column.key,
    label: column.label,
  }));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DataTableToolbar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        showClearAll={showClearAll}
        onClearAll={onClearAll}
        activeFilterCount={activeFilterCount}
        onOpenColumns={() => setColumnsOpen(true)}
        onOpenFilters={() => setFiltersOpen(true)}
        extraActions={toolbarActions}
        exporting={exporting}
        showExport={showExport}
        onExportXLSX={onExportXLSX ?? (() => undefined)}
        onExportCSV={onExportCSV ?? (() => undefined)}
      />

      {filterSheet ? (
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DataTableDrawerSheet
            title={filterSheet.title ?? 'Filters'}
            onClose={() => setFiltersOpen(false)}
            onReset={filterSheet.onReset}
            resetDisabled={filterSheet.resetDisabled}
            onApply={() => setFiltersOpen(false)}
            footer="actions"
          >
            {filterSheet.content}
          </DataTableDrawerSheet>
        </Sheet>
      ) : null}

      <Sheet open={columnsOpen} onOpenChange={setColumnsOpen}>
        <DataTableDrawerSheet
          title="Columns"
          onClose={() => setColumnsOpen(false)}
          onReset={resetColumns}
          onApply={() => setColumnsOpen(false)}
          footer="apply"
          scrollBody={false}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnsDragEnd}
          >
            <DataTableColumnVisibilityList
              items={columnPrefs.order}
              columns={columnDrawerColumns}
              visibleKeys={columnPrefs.visible}
              onToggle={(key) => toggleColumn(key as TKey)}
            />
          </DndContext>
        </DataTableDrawerSheet>
      </Sheet>

      <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto px-4">
        {isError ? (
          <div className="h-64">
            <CollectionErrorState title={errorTitle} onRetry={onRetry} />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleHeaderDragEnd}
          >
            <table
              ref={tableRef}
              className="w-full table-fixed text-sm"
              style={{ minWidth: tableMinWidth }}
            >
              <colgroup>
                {visibleColumns.map((column) => (
                  <col key={column.key} style={{ width: visibleColumnWidths.get(column.key) }} />
                ))}
                {hasActions ? <col style={{ width: measuredActionsWidth }} /> : null}
              </colgroup>
              <thead className="sticky top-0 z-10 bg-muted">
                <SortableContext
                  items={visibleColumns.map((column) => column.key)}
                  strategy={horizontalListSortingStrategy}
                >
                  <tr className="border-b border-border/60 bg-muted">
                    {visibleColumns.map((column, index) => (
                      <SortableEntityHeader
                        key={column.key}
                        column={column}
                        isFirst={index === 0}
                        width={visibleColumnWidths.get(column.key)}
                        sortDir={sortKey === column.key ? (sortDir ?? null) : null}
                        onSort={() => onSort?.(column)}
                        onResizeStart={handleResizeStart}
                      />
                    ))}
                    {hasActions ? (
                      <th
                        className="sticky right-0 z-20 bg-muted py-2.5 pr-4 shadow-[-1px_0_0_0_var(--border)]"
                        style={{ width: measuredActionsWidth }}
                      />
                    ) : null}
                  </tr>
                </SortableContext>
              </thead>
              <tbody>
                {isLoading ? (
                  DataTableSkeletonRows({
                    rowCount: skeletonRowCount,
                    columnCount: visibleColumns.length,
                    actionCellClassName: hasActions ? actionCellClassName : 'hidden',
                  })
                ) : rows.length === 0 ? (
                  <DataTableEmptyRow
                    colSpan={visibleColumns.length + (hasActions ? 1 : 0)}
                    icon={emptyIcon}
                    title={emptyTitle}
                  />
                ) : (
                  rows.map((row) => (
                    <tr
                      key={getRowKey(row)}
                      className={cn(
                        // The hover tint lives on the cells (not the row) so the
                        // opaque sticky columns don't composite `muted/40` twice
                        // (over their own bg-card AND the row) and render darker —
                        // that double-tint was the visible hover seam.
                        'group transition-colors',
                        onRowClick && 'cursor-pointer',
                        typeof rowClassName === 'function' ? rowClassName(row) : rowClassName,
                      )}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {visibleColumns.map((column, index) => {
                        const cellContent = renderCell(column.key, row, column);
                        // Single-line truncate + a `title` (full value on hover) for
                        // string cells keeps every row the same height — only opt-in
                        // `wrap` columns go multi-line. Mirrors the header's ellipsis.
                        const cellTitle = typeof cellContent === 'string' ? cellContent : undefined;
                        return (
                          <td
                            key={column.key}
                            title={cellTitle}
                            className={cn(
                              'border-b border-border/40 px-3 py-2.5 text-sm font-normal group-hover:bg-muted/40',
                              index === 0 && 'pl-4',
                              column.align === 'right' && 'text-right',
                              column.align === 'center' && 'text-center',
                              'overflow-hidden',
                              index === 0 &&
                                'sticky left-0 z-[5] bg-card shadow-[1px_0_0_0_var(--border)] group-hover:bg-muted/40',
                              column.cellClassName,
                            )}
                          >
                            <div
                              className={cn(
                                'min-w-0',
                                column.wrap ? 'whitespace-normal break-words' : 'truncate',
                                column.align === 'right' && 'flex justify-end',
                                column.align === 'center' && 'flex justify-center',
                              )}
                            >
                              {cellContent}
                            </div>
                          </td>
                        );
                      })}
                      {hasActions ? (
                        <td
                          className={cn(
                            actionCellClassName,
                            'sticky right-0 z-[5] bg-card shadow-[-1px_0_0_0_var(--border)] group-hover:bg-muted/40',
                          )}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div data-actions-content className="inline-flex items-center">
                            {renderRowActions!(row)}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DndContext>
        )}
      </div>

      {!isLoading && pagination && pagination.count > 0 ? (
        pagination.variant === 'entity-list' &&
        pagination.pageSize &&
        pagination.count > pagination.pageSize ? (
          <EntityListPagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.count}
            onPrevious={pagination.onPrevious}
            onNext={pagination.onNext}
          />
        ) : pagination.variant === 'entity-list' ? (
          <DataTableCountFooter
            count={pagination.count}
            singular={pagination.singular}
            plural={pagination.plural}
          />
        ) : (
          <DataTablePaginationFooter
            count={pagination.count}
            singular={pagination.singular}
            plural={pagination.plural}
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPrevious={pagination.onPrevious}
            onNext={pagination.onNext}
          />
        )
      ) : null}
    </div>
  );
}

export { EntityDataTable as FinancialDataTable };
export type FinancialDataTableColumn<TKey extends string = string> = EntityDataTableColumn<TKey>;
export type FinancialDataTableSortDir = EntityDataTableSortDir;
